import React, { useMemo } from "react";
import { FolderPlus } from "lucide-react";
import {
  AppButton, AppCard, AppCardContent, AppCardDescription, AppCardHeader, AppCardTitle,
  Callout, Checkbox, FormField, NativeSelect,
} from "../../ui";

/**
 * Step 1 — where the question goes: a category and, when it has them, a
 * subcategory. Assessments draw questions by these, so a question with no topic
 * cannot be saved. A missing category is made here, in a dialog, without
 * leaving the question behind.
 */
export default function TopicStep({ form, set, setForm, categories, errors, isLibrary, onNewCategory }) {
  const top = useMemo(() => categories.filter((c) => !c.parentCategory), [categories]);
  const subs = useMemo(
    () => (form.category ? categories.filter((c) => String(c.parentCategory || "") === form.category) : []),
    [categories, form.category],
  );

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <AppCard>
        <AppCardHeader>
          <AppCardTitle>Topic</AppCardTitle>
          <AppCardDescription>
            The category is the folder it lives in. A subcategory narrows it down — assessments can draw random
            questions from either.
          </AppCardDescription>
        </AppCardHeader>
        <AppCardContent className="tw-flex tw-flex-col tw-gap-4">
          {top.length === 0 && (
            <Callout
              tone="info"
              action={(
                <AppButton type="button" size="xs" variant="outline" onClick={() => onNewCategory(null)}>
                  Create a category
                </AppButton>
              )}
            >
              Your question bank has no categories yet. Create the first one — e.g. Aptitude, Python or ICD-10.
            </Callout>
          )}

          <div className="tw-grid tw-gap-4 min-[768px]:tw-grid-cols-2">
            <FormField label="Category" required error={errors.category}>
              <NativeSelect
                value={form.category}
                onChange={(e) => setForm((f) => ({ ...f, category: e.target.value, subcategory: "" }))}
              >
                <option value="">Choose a category</option>
                {top.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
              </NativeSelect>
            </FormField>

            <FormField
              label="Subcategory"
              hint={form.category && subs.length === 0 ? "This category has no subcategories." : "Optional."}
            >
              <NativeSelect
                value={form.subcategory}
                onChange={(e) => set("subcategory", e.target.value)}
                disabled={!form.category || subs.length === 0}
              >
                <option value="">{form.category && subs.length ? "None — the category itself" : "—"}</option>
                {subs.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
              </NativeSelect>
            </FormField>
          </div>

          <div className="tw-flex tw-flex-wrap tw-gap-2">
            <AppButton type="button" size="sm" variant="ghost" onClick={() => onNewCategory(null)}>
              <FolderPlus size={16} aria-hidden="true" /> New category
            </AppButton>
            {form.category && (
              <AppButton type="button" size="sm" variant="ghost" onClick={() => onNewCategory(form.category)}>
                <FolderPlus size={16} aria-hidden="true" /> New subcategory here
              </AppButton>
            )}
          </div>
        </AppCardContent>
      </AppCard>

      {isLibrary && (
        <AppCard>
          <AppCardContent>
            <Checkbox
              checked={form.isPublic}
              onChange={(e) => set("isPublic", e.target.checked)}
              label="Available in the marketplace"
              description="Untick to hold this question back even when its folder is published."
            />
          </AppCardContent>
        </AppCard>
      )}
    </div>
  );
}
